import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useParams, useNavigate } from 'react-router-dom';
import config from './config';
import { MathJaxContext, MathJax } from 'better-react-mathjax';

interface PracticeOption {
  id: number;
  questionId: number;
  optionText: string;
  isCorrect: boolean;
  orderIndex: number;
}

interface PracticeQuestion {
  id: number;
  practiceTestId: number;
  questionText: string;
  questionLatex?: string;
  questionType: string;
  difficulty: string;
  marks: number;
  orderIndex: number;
  explanation: string;
  options: PracticeOption[];
}

interface PracticeTestDetail {
  id: number;
  courseId: number;
  name: string;
  description: string;
  timeLimit: number;
  passingScore: number;
  totalQuestions: number;
  isActive: boolean;
  questions: PracticeQuestion[];
}

interface Answer {
  questionId: number;
  selectedOptionId: number | null;
  answerText: string;
}

export const PracticeTestQuiz: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const [test, setTest] = useState<PracticeTestDetail | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attemptId, setAttemptId] = useState<number | null>(null);
  const [testStarted, setTestStarted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const prepareForMathJax = (value?: string | null): string => {
    const text = (value || '').trim();
    if (!text) return '';

    const hasDelimiters = /\$|\\\(|\\\[/.test(text);
    const looksLikeLatex = /\\[a-zA-Z]+|\^|_/.test(text);

    if (!hasDelimiters && looksLikeLatex) {
      return `\\(${text}\\)`;
    }

    return text;
  };

  const toHtmlMarkup = (value?: string | null): string => {
    const prepared = prepareForMathJax(value);
    if (!prepared) return '';

    const hasHtmlTags = /<\/?[a-z][\s\S]*>/i.test(prepared);
    if (hasHtmlTags) {
      return prepared;
    }

    const escaped = prepared
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    return escaped.replace(/\r?\n/g, '<br/>');
  };

  useEffect(() => {
    fetchTestDetails();
  }, [id]);

  useEffect(() => {
    if (!testStarted || isSubmitting) return;
    
    if (timeRemaining > 0) {
      const timer = setTimeout(() => setTimeRemaining(timeRemaining - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeRemaining === 0 && test && attemptId) {
      // Auto-submit when time runs out
      handleSubmitTest();
    }
  }, [timeRemaining, testStarted, test, attemptId, isSubmitting]);

  const fetchTestDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${config.apiBaseUrl}/api/practicetests/${id}`
      );
      if (!response.ok) throw new Error('Failed to fetch test details');
      const data = await response.json();
      setTest(data);
      setTimeRemaining(data.timeLimit * 60);
      // Initialize answers with the fetched questions
      const initialAnswers = data.questions.map((q: PracticeQuestion) => ({
        questionId: q.id,
        selectedOptionId: null,
        answerText: '',
      }));
      setAnswers(initialAnswers);
      setError(null);
    } catch (err) {
      setError('Failed to load practice test');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const startTest = async (testId: number) => {
    try {
      console.log('Starting test with ID:', testId);
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${config.apiBaseUrl}/api/practiceattempts/start`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ practiceTestId: testId }),
        }
      );
      console.log('Start test response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('Attempt created:', data);
        setAttemptId(data.id);
        setTestStarted(true);
      } else {
        const error = await response.json().catch(() => ({}));
        console.error('Failed to start test:', error);
        alert('Failed to start test');
      }
    } catch (err) {
      console.error('Failed to start test:', err);
      alert('Error starting test');
    }
  };

  const handleSelectOption = (optionId: number) => {
    const currentQuestion = test?.questions[currentQuestionIndex];
    if (currentQuestion && answers.length > 0) {
      const updatedAnswers = [...answers];
      const answerIndex = updatedAnswers.findIndex(
        (a) => a.questionId === currentQuestion.id
      );
      if (answerIndex >= 0) {
        updatedAnswers[answerIndex] = {
          ...updatedAnswers[answerIndex],
          selectedOptionId: optionId,
          answerText: '',
        };
        setAnswers(updatedAnswers);
      }
    }
  };

  const handleTextAnswer = (text: string) => {
    const currentQuestion = test?.questions[currentQuestionIndex];
    if (currentQuestion && answers.length > 0) {
      const updatedAnswers = [...answers];
      const answerIndex = updatedAnswers.findIndex(
        (a) => a.questionId === currentQuestion.id
      );
      if (answerIndex >= 0) {
        updatedAnswers[answerIndex] = {
          ...updatedAnswers[answerIndex],
          answerText: text,
          selectedOptionId: null,
        };
        setAnswers(updatedAnswers);
      }
    }
  };

  const handleSubmitTest = async () => {
    if (!attemptId || !test || isSubmitting) {
      console.error('Cannot submit:', { attemptId, test: !!test, isSubmitting });
      return;
    }

    setIsSubmitting(true);

    try {
      const token = await getAccessTokenSilently();
      console.log('About to submit test with:', {
        practiceTestId: test.id,
        timeSpent: Math.max(0, test.timeLimit * 60 - timeRemaining),
        answersCount: answers.length,
        answers: answers.slice(0, 3), // Log first 3 for debugging
      });

      const requestBody = {
        practiceTestId: test.id,
        timeSpent: Math.max(0, test.timeLimit * 60 - timeRemaining),
        answers: answers.map((a) => ({
          questionId: a.questionId,
          selectedOptionId: a.selectedOptionId || null,
          answerText: a.answerText || null,
        })),
      };

      console.log('Request body:', JSON.stringify(requestBody, null, 2));

      const response = await fetch(
        `${config.apiBaseUrl}/api/practiceattempts/submit`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(requestBody),
        }
      );

      console.log('Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          message: response.statusText,
        }));
        console.error('Submit response error:', response.status, errorData);
        alert(
          `Error submitting test: ${errorData.message || response.statusText}`
        );
        setIsSubmitting(false);
        return;
      }

      const result = await response.json();
      console.log('Test submitted successfully:', result);
      navigate(`/practice-result/${attemptId}`, { state: { result } });
    } catch (err) {
      console.error('Failed to submit test:', err);
      alert(`Error: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setIsSubmitting(false);
    }
  };

  if (loading)
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );

  if (error || !test)
    return (
      <div className="min-h-screen flex items-center justify-center bg-red-50">
        <div className="bg-white p-8 rounded-lg shadow">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="text-gray-600 mb-6">{error || 'Test not found'}</p>
          <button
            onClick={() => navigate(-1)}
            className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );

  if (!testStarted)
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Ready to Start?</h2>
          <p className="text-gray-600 mb-6">{test?.name}</p>
          <div className="space-y-2 mb-6 bg-gray-50 p-4 rounded">
            <p>
              <strong>Time Limit:</strong> {test?.timeLimit} minutes
            </p>
            <p>
              <strong>Questions:</strong> {test?.totalQuestions}
            </p>
            <p>
              <strong>Passing Score:</strong> {test?.passingScore}%
            </p>
          </div>
          <button
            onClick={() => {
              if (test?.id) {
                startTest(test.id);
              }
            }}
            className="w-full bg-blue-600 text-white py-3 rounded font-semibold hover:bg-blue-700"
          >
            Start Test
          </button>
        </div>
      </div>
    );

  const currentQuestion = test?.questions[currentQuestionIndex];
  if (!currentQuestion) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-red-50">
        <div className="bg-white p-8 rounded-lg shadow">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="text-gray-600 mb-6">Failed to load question</p>
          <button
            onClick={() => navigate(-1)}
            className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const currentAnswer = answers.find((a) => a.questionId === currentQuestion?.id) || null;

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <MathJaxContext>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-3xl font-bold text-gray-800">{test.name}</h1>
            <div
              className={`text-2xl font-bold p-4 rounded ${
                timeRemaining < 300
                  ? 'bg-red-100 text-red-600'
                  : 'bg-blue-100 text-blue-600'
              }`}
            >
              {formatTime(timeRemaining)}
            </div>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className="bg-blue-600 h-3 rounded-full transition-all"
              style={{
                width: `${((currentQuestionIndex + 1) / test.questions.length) * 100}%`,
              }}
            ></div>
          </div>
          <p className="text-gray-600 mt-2">
            Question {currentQuestionIndex + 1} of {test.questions.length}
          </p>
        </div>

        {/* Question */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
          <div className="flex items-start gap-4">
            <div
              className={`text-2xl font-bold px-3 py-1 rounded ${
                currentQuestion.difficulty === 'Easy'
                  ? 'bg-green-100 text-green-800'
                  : currentQuestion.difficulty === 'Medium'
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-red-100 text-red-800'
              }`}
            >
              {currentQuestion.difficulty}
            </div>
            <div className="text-2xl font-bold text-gray-800 flex-1">
              {currentQuestion.questionLatex ? (
                <div className="bg-gray-50 p-4 rounded-lg text-lg">
                  <MathJax>{`$$\\displaystyle ${currentQuestion.questionLatex}$$`}</MathJax>
                </div>
              ) : (
                currentQuestion.questionText
              )}
            </div>
          </div>

          {/* Options */}
          <div className="mt-8 space-y-3">
            {currentQuestion.questionType === 'MCQ' ||
            currentQuestion.questionType === 'TrueFalse' ? (
              currentQuestion.options.map((option) => (
                <button
                  key={option.id}
                  onClick={() => handleSelectOption(option.id)}
                  className={`w-full p-4 text-left border-2 rounded-lg transition-colors ${
                    currentAnswer?.selectedOptionId === option.id
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-400'
                  }`}
                >
                  <span className="flex items-center gap-3">
                    <span
                      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        currentAnswer?.selectedOptionId === option.id
                          ? 'bg-blue-600 border-blue-600'
                          : 'border-gray-300'
                      }`}
                    >
                      {currentAnswer?.selectedOptionId === option.id && (
                        <span className="w-3 h-3 bg-white rounded-full"></span>
                      )}
                    </span>
                    <MathJax inline dynamic>
                      <span dangerouslySetInnerHTML={{ __html: toHtmlMarkup(option.optionText) }} />
                    </MathJax>
                  </span>
                </button>
              ))
            ) : (
              <textarea
                value={currentAnswer?.answerText || ''}
                onChange={(e) => handleTextAnswer(e.target.value)}
                placeholder="Type your answer here..."
                className="w-full p-4 border-2 border-gray-200 rounded-lg focus:border-blue-600 focus:outline-none"
                rows={5}
              />
            )}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex gap-4 justify-between">
          <button
            onClick={() => setCurrentQuestionIndex(Math.max(0, currentQuestionIndex - 1))}
            disabled={currentQuestionIndex === 0}
            className="px-6 py-3 bg-gray-600 text-white rounded font-semibold hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>

          <div className="flex gap-2 flex-wrap justify-center">
            {test.questions.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentQuestionIndex(idx)}
                className={`w-10 h-10 rounded font-semibold transition-colors ${
                  idx === currentQuestionIndex
                    ? 'bg-blue-600 text-white'
                    : answers[idx]?.selectedOptionId ||
                      answers[idx]?.answerText
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-300 text-gray-700'
                }`}
              >
                {idx + 1}
              </button>
            ))}
          </div>

          {currentQuestionIndex === test.questions.length - 1 ? (
            <button
              onClick={handleSubmitTest}
              disabled={!attemptId}
              className="px-6 py-3 bg-green-600 text-white rounded font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Submit Test
            </button>
          ) : (
            <button
              onClick={() => setCurrentQuestionIndex(Math.min(test.questions.length - 1, currentQuestionIndex + 1))}
              className="px-6 py-3 bg-blue-600 text-white rounded font-semibold hover:bg-blue-700"
            >
              Next
            </button>
          )}
        </div>
      </div>
    </div>
    </MathJaxContext>
  );
};
